import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { fetchProfileDataAction } from '../../../store/actions/profile';
import { addCryptoWallet } from '../../../services/backendAPI';
import { INITIAL_CRYPTO_FORM_DATA } from '../constants/cryptoModalConstants';

const useCryptoWalletForm = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const userData = useSelector(state => state.profile.userData);

    const [formData, setFormData] = useState({
        ...INITIAL_CRYPTO_FORM_DATA,
        client_id: localStorage.getItem('login_id')
    });

    useEffect(() => {
        if (userData?.phone_number) {
            setFormData(prev => ({ ...prev, phone_number: userData.phone_number }));
        }
    }, [userData]);

    const handleChange = useCallback((e, onClearError) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            // Auto-reset network if currency changes since networks depend on currency
            if (name === 'currency') {
                updated.network = '';
            }
            return updated;
        });

        if (onClearError) {
            onClearError(name);
        }
    }, []);

    const updateFormData = useCallback((updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData({
            ...INITIAL_CRYPTO_FORM_DATA,
            client_id: localStorage.getItem('login_id'),
            phone_number: userData?.phone_number || ''
        });
    }, [userData]);

    const handleSubmit = useCallback(async (validateForm, onError, isOtpSent, paymentMethods) => {
        if (!validateForm(formData, isOtpSent, paymentMethods)) return;

        setLoading(true);
        try {
            const response = await addCryptoWallet(formData);

            if (response?.status === 'success') {
                dispatch(fetchProfileDataAction());
                enqueueSnackbar(response?.message || 'Crypto wallet added successfully', {
                    variant: 'success'
                });
                return true; // Success
            } else {
                onError('submit', response?.message || 'Failed to add crypto wallet');
                return false;
            }
        } catch (error) {
            enqueueSnackbar(error?.message || error?.toString() || 'Failed to add crypto wallet', {
                variant: 'error',
                autoHideDuration: 3000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                }
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [formData, dispatch, enqueueSnackbar]);

    return {
        formData,
        loading,
        handleChange,
        updateFormData,
        resetForm,
        handleSubmit
    };
};

export default useCryptoWalletForm;
